import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Grid, Button, Checkbox } from '@material-ui/core';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import shallow from 'zustand/shallow';
import { useConfirm } from "material-ui-confirm";

import AuthContext from '../../Auth/AuthContext';
import useStore from '../../Base/Store';
import { getDataList, getEntityList, getFilteredEntityList } from '../../Base/DataProvider';
import { PageHeader, AdaptiveButton } from '../../UI/Controls/Controls';
import SelectParty from '../../UI/Controls/Business/SelectParty';
import MuiTable from '../../UI/Controls/MuiTable';
import AppIcon from '../../UI/Controls/AppIcons';
import { DateInput } from '../../UI/Controls/InputFields';
import {
    safeInt,
    safeString,
    formatCurrencyAmount,
    getLocaleDate,
    getFinancialStartDate,
    getFinancialEndDate,
    validateDateRange,
    isNullOrUndefined,
    getStartOfMonth,
    getConfirmOptions
} from '../../Base/Utils';
import {
    CUSTOMER_ENTITY,
    SUPPLIER_ENTITY,
    SALES_ORDER_ENTITY,
    PURCHASE_ORDER_ENTITY,
    ERROR_MAX_DATE_RANGE
} from '../../Base/Common';
import { isSalesVoucher } from '../OrderItemHelper';
import useStyles from '../../../styles';
import WebAPI from '../../Base/WebAPI';

const validationSchema = yup.object({
    fromDate: yup.date().required('From date is required'),
    toDate: yup.date().required('To date is required'),
    referenceId: yup.number().min(1, 'Please select a party')
});

export default function SelectOrdersForConversion() {
    const { entityName } = useParams();
    const history = useHistory();
    const classes = useStyles();
    const authContext = useContext(AuthContext);
    const showAlert = useStore(state => state.showAlert);
    const [waiting, setWaiting] = useStore(state => [state.waiting, state.setWaiting], shallow);
    const confirm = useConfirm();

    const [orders, setOrders] = useState([]);
    const [parties, setParties] = useState([]);
    const [selectedParty, setSelectedParty] = useState(null);
    const [selectedOrders, setSelectedOrders] = useState([]);

    const currencyCode = safeString(authContext.currentSubscriber.currencyCode);

    // Determine reference type based on entity name
    const referenceType = isSalesVoucher(entityName) ? CUSTOMER_ENTITY : SUPPLIER_ENTITY;

    const [fieldValues, setFieldValues] = useState({
        fromDate: getStartOfMonth(),
        toDate: getFinancialEndDate(),
        referenceId: 0,
        referenceName: ''
    });

    useEffect(() => {
        let source = axios.CancelToken.source();

        async function init() {
            try {
                setWaiting(true);
                // Load parties based on reference type
                const partyList = await getEntityList(referenceType, authContext, source.token, true);
                if (partyList && !partyList.code) {
                    setParties(partyList);
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.log(error);
                    showAlert('Failed to load party list', 'error');
                }
            } finally {
                setWaiting(false);
            }
        }

        init();

        return () => {
            source.cancel();
        };
    }, [entityName]);

    const getOrderList = async (values, token = null) => {
        try {
            setWaiting(true);

            const filter = {
                fromDate: values.fromDate,
                toDate: values.toDate,
                referenceId: safeInt(values.referenceId),
                referenceType: referenceType
            };

            const response = await getDataList(entityName, `Get${entityName}List`, filter, authContext, token, 'put');            
            if (response) {
                if (response.code && response.code > 0) {
                    showAlert(response.message, 'error');
                    setOrders([]);
                } else {
                    // Filter out already converted orders if needed
                    setOrders(Array.isArray(response) ? response : []);
                    setSelectedOrders([]);
                }
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.log(error);
                showAlert('Failed to load order list', 'error');
            }
            setOrders([]);
        } finally {
            setWaiting(false);
        }
    };

    const handlePartyChange = async (value, setFieldValue, values) => {
        if (value) {
            setSelectedParty(value);
            setFieldValue('referenceId', safeInt(value.id));
            setFieldValue('referenceName', safeString(value.legalName || value.name));

            // Load orders for selected party
            const updatedValues = {
                ...values,
                referenceId: safeInt(value.id),
                referenceName: safeString(value.legalName || value.name)
            };
            await getOrderList(updatedValues);
        } else {
            setSelectedParty(null);
            setFieldValue('referenceId', 0);
            setFieldValue('referenceName', '');
            setOrders([]);
        }
    };

    const submitFormData = async (values) => {
        try {
            if (validateDateRange(values.fromDate, values.toDate)) {
                showAlert(ERROR_MAX_DATE_RANGE, 'error');
                return;
            }

            await getOrderList(values);
        } catch (error) {
            console.log(error);
            showAlert('Failed to load orders', 'error');
        }
    };

    const handleConvert = async () => {
        if (selectedOrders.length === 0) {
            showAlert('Please select at least one order to convert', 'warning');
            return;
        }
        if (selectedOrders.length === 1) {
            // single order selected, redirect to normal conversion page
            history.push(`/invoicesummary/${entityName}/${selectedOrders[0].id}`);
            return;            
        }
        confirm(getConfirmOptions(`Convert ${entityName} to invoice`, `Are you sure, you want to convert the selected ${entityName}(s) into a sales invoice?`))
            .then(async () => {
                try {
                    setWaiting(true);
                    const orderIds = selectedOrders.map(order => safeInt(order.id));
                    // concatenate orderNumber as orderReference
                    const orderReference = selectedOrders.map(order => safeString(order.orderNumber)).join(', ');
                    const request = {
                        sourceOrderIds: orderIds, 
                        orderReference: orderReference
                    };
                    const response = await WebAPI.post(`/${entityName}/ConvertMultipleToInvoice`, request, { headers: { "AccessToken": authContext.token }, cancelToken: null });
                    if (safeInt(response.data.code) === 0) {
                        showAlert(`${selectedOrders.length} ${entityName}(s) converted successfully`, 'success');
                        setTimeout(() => {
                            history.push(`/invoice/${response.data.id}`);    
                        }, 500);
                        
                    } else {
                        showAlert('Failed to convert orders', 'error');
                    }
                } catch (error) {
                    showAlert(entityName + ' convert to invoice failed. Internal server error.', 'error');
                } finally {
                    setWaiting(false);
                }
            }).catch(() => { });
    };

    const columns = [
        {
            title: 'Voucher No',
            field: 'orderNumber',
            render: (rowData) => safeString(rowData.orderNumber),
            alwaysVisible: true
        },
        {
            title: 'Date',
            field: 'orderDate',
            render: (rowData) => getLocaleDate(rowData.orderDate),
            alwaysVisible: true
        },
        {
            title: referenceType === CUSTOMER_ENTITY ? 'Customer' : 'Supplier',
            field: 'referenceName',
            render: (rowData) => safeString(rowData.referenceName)
        },
        {
            title: 'Total',
            field: 'orderTotal',
            type: 'numeric',
            render: (rowData) => formatCurrencyAmount(currencyCode, rowData.orderTotal)
        },
        {
            title: 'Tax Amount',
            field: 'taxAmount',
            type: 'numeric',
            render: (rowData) => formatCurrencyAmount(currencyCode, rowData.taxAmount)
        },
        {
            title: 'Grand Total',
            field: 'grandTotal',
            type: 'numeric',
            render: (rowData) => formatCurrencyAmount(currencyCode, rowData.grandTotal),
            alwaysVisible: true
        }
    ];

    const actionDefintion = [
        {
            icon: () => <><Button variant='contained' color='primary' startIcon={<AppIcon name="Copy" />}>Convert selected</Button></>,
            tooltip: 'Convert Selected',
            onClick: handleConvert,
            disabled: selectedOrders.length === 0
        }
    ];

    return (
        <div style={{ padding: '16px' }}>
            {/* <PageHeader 
                title={`Select ${entityName} for Conversion`}
                icon="Invoice"
            /> */}

            <PageHeader heading={`Select ${entityName}(s) for Conversion`}>
                <AppIcon name={'Copy'} color={"primary"} />
            </PageHeader>

            <Formik
                initialValues={fieldValues}
                validationSchema={validationSchema}
                onSubmit={submitFormData}
                enableReinitialize
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        <Grid container spacing={2} style={{ marginBottom: '16px' }}>
                            <Grid item xs={12} sm={4}>
                                <SelectParty
                                    parties={parties}
                                    selectedParty={selectedParty}
                                    handleSelectionChanged={(e, value) =>
                                        handlePartyChange(value, setFieldValue, values)
                                    }
                                    referenceType={referenceType}
                                    isRequired={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <DateInput
                                    label="From Date"
                                    name="fromDate"
                                    value={values.fromDate}
                                    onChange={(date) => setFieldValue('fromDate', date)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <DateInput
                                    label="To Date"
                                    name="toDate"
                                    value={values.toDate}
                                    onChange={(date) => setFieldValue('toDate', date)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2} style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<AppIcon name="Search" />}
                                    fullWidth
                                >
                                    Search
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>

            <MuiTable
                columns={columns}
                data={orders}
                search={true}
                sorting={true}
                paging={true}
                exportButton={true}
                exportAllData={true}
                filtering={false}
                selection={true}
                actions={selectedOrders.length > 0 ? actionDefintion : []}
                onSelectionChange={(rows) => setSelectedOrders(rows)}
            />
        </div>
    );
}
